import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Prompt, useHistory } from 'react-router-dom'

import { SolidAlert } from 'v2/ui/svgs'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import {
    Modal,
    ModalContent,
    ModalContentInner,
    ModalFooter,
    ModalFooterButtonGroup,
    ModalHeader,
} from 'ui/components/Modal'
import { Body } from 'ui/components/Text'

export type UnsavedChangesCloseGuard = {
    tryClose: () => Promise<boolean>
}

type Props = {
    isDirty: boolean
    dirtyFromFormContext?: boolean
    onContinueWithoutSaving?: () => void
    onCancel?: () => void
}

export const GenericUnsavedChangesModal = forwardRef<UnsavedChangesCloseGuard, Props>(
    ({ isDirty, onContinueWithoutSaving: onContinue, onCancel: handleCancel }: Props, ref) => {
        const [location, setLocation] = useState<Location | null>(null)
        const [isOpen, setIsOpen] = useState(false)

        const history = useHistory()
        const resolveTryClosePromise = useRef<((value: boolean) => void) | null>(null)

        useImperativeHandle<UnsavedChangesCloseGuard, UnsavedChangesCloseGuard>(ref, () => ({
            tryClose: () => {
                return new Promise<boolean>((resolve) => {
                    if (isDirty) {
                        setIsOpen(true)
                        setLocation(null)
                        resolveTryClosePromise.current = resolve
                    } else {
                        resolve(true)
                    }
                })
            },
        }))

        const blockNavigation = isDirty
        const navigatingTo = useRef<Location | null>(null)

        const onNavigateInternally = (nextLocation: Location) => {
            if (
                nextLocation.pathname === history.location.pathname ||
                nextLocation.pathname === navigatingTo.current?.pathname
            ) {
                return true
            }

            setIsOpen(true)
            setLocation(nextLocation)
            return false
        }

        const handleContinueWithoutSaving = () => {
            setIsOpen(false)

            if (onContinue) {
                onContinue()
            }

            if (location) {
                navigatingTo.current = location
                history.push(location)
                setLocation(null)
                return
            } else if (resolveTryClosePromise.current) {
                resolveTryClosePromise.current(true)
            }
        }

        const handleCancelNavigation = () => {
            setLocation(null)
            setIsOpen(false)
            if (handleCancel) {
                handleCancel()
            }

            if (resolveTryClosePromise.current) {
                resolveTryClosePromise.current(false)
            }
        }

        useEffect(() => {
            const onBeforeUnload = (event: BeforeUnloadEvent) => {
                if (!blockNavigation) {
                    return
                }

                event.preventDefault()
                event.returnValue = ''
            }

            window.addEventListener('beforeunload', onBeforeUnload)

            return () => {
                window.removeEventListener('beforeunload', onBeforeUnload)
            }
        }, [blockNavigation])

        return (
            <>
                {/*@ts-expect-error*/}
                <Prompt when={blockNavigation} message={onNavigateInternally} />
                <Modal open={isOpen}>
                    <ModalContent>
                        <ModalHeader showCloseButton={false} />
                        <ModalContentInner>
                            <Box flex column alignItems="center" gap="m">
                                <SolidAlert />
                                <Body size="l" weight="medium">
                                    You have unsaved changes
                                </Body>
                                <Body size="m" weight="regular">
                                    Are you sure you want to exit without saving?
                                </Body>
                            </Box>
                        </ModalContentInner>

                        <ModalFooter>
                            <ModalFooterButtonGroup>
                                <Button size="l" variant="primary" onClick={handleCancelNavigation}>
                                    Stay on this page
                                </Button>
                                <Button
                                    size="l"
                                    variant="secondary"
                                    onClick={handleContinueWithoutSaving}
                                >
                                    Exit without saving
                                </Button>
                            </ModalFooterButtonGroup>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </>
        )
    }
)

GenericUnsavedChangesModal.displayName = 'GenericUnsavedChangesModal'

export const FormContextUnsavedChangesModal = forwardRef<
    UnsavedChangesCloseGuard,
    Omit<Props, 'isDirty'>
>((props, ref) => {
    const {
        formState: { isDirty: isFormDirty },
    } = useFormContext()
    return <GenericUnsavedChangesModal isDirty={isFormDirty} {...props} ref={ref} />
})

FormContextUnsavedChangesModal.displayName = 'FormContextUnsavedChangesModal'
